import React, { ReactElement } from 'react';
import State from 'store/State';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { OrderColumns } from 'communication/types/order/Order';
import { PortfolioInput } from 'portfolio/PortfolioInput';
import { SetConfigAction } from './store/actions';
import { WidgetSharedStateSection } from './store/shared/WidgetsSharedState';
import { AppModeWidgetCategory, WidgetCategory } from './WidgetCategory';
import { WidgetStreamConfig } from 'widgets/streaming/WidgetStreamConfig';
import { PortfolioSelectionRequirements } from 'widgets/PortfolioSelectionRequirements';
import { WidgetComponentProps } from 'widgets/WidgetComponentProps';
import {
    WidgetCustomManagedConfig,
    WidgetRestByRequestUidConfig,
    WidgetStreamByRequestUidConfig,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { PortfolioItemColumn } from 'portfolio/PortfolioItem';
import { PortfolioSelection } from 'portfolio/PortfolioSelection';
import { SymbolsService } from 'symbols/SymbolsService';
import { DropdownTooltipText } from '@instinet-analytics/semantic-ui-components';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyActionType = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyConfig = any;

export type GetRequiredOrderColumns = (
    state: State,
    widgetId: string,
    symbology: SymbologyValues
) => OrderColumns;

export type GetRequiredPortfolioColumns = (
    state: State,
    widgetId: string,
    symbology: SymbologyValues
) => PortfolioItemColumn[];

export type UpdateConfigAction<TConfig> = (
    id: string,
    config: TConfig
) => SetConfigAction<AnyActionType, TConfig>;

export type GetConfigSelector<TConfig> = (state: State, id: string) => TConfig;

export interface RenderFilterOptions {
    column?: string;
    element?: React.ReactElement;
}
export type RenderFilter = (
    widgetId: string,
    options?: RenderFilterOptions
) => ReactElement;

export type WidgetHost = 'layout' | 'tile';

/*
 * Strategy interface implemented by each widget and injected into the WidgetsService keyed by WidgetType
 */
export interface WidgetStrategy {
    /*
     * Appears in "Add Widget" drop down menu and tab title
     */
    title: string;
    /*
     * The hosts that this widget can be rendered in
     */
    supportedHosts: WidgetHost[];
    /*
     * The component type rendered for this widget
     */
    reactComponentType: React.FC<WidgetComponentProps>;
    /*
     * True if this is an input widget
     */
    isInput?: boolean;
    /*
     * Widgets that own a filter (i.e. a blotter) implement this to render a filter used in the ActiveFiltersIndicator
     */
    renderFilter?: RenderFilter;
    /*
     * If this widget needs columns in the orders stream, it implements this function
     */
    getRequiredOrderColumns?: GetRequiredOrderColumns;
    /*
     * If this widget needs columns in the portfolio, it implements this function
     */
    getRequiredPortfolioColumns?: GetRequiredPortfolioColumns;
    /*
     *  Return true to hide this within a Newport linkjed palette
     */
    hiddenInNewportLinkedPalette?: boolean;
    /*
     * Authorities the user needs to access this widget
     */
    requiredAuthorities?: string[];
    /*
     *  List of shared state sections that this widget type can update
     */
    sharedStateSections?: WidgetSharedStateSection[];
    /*
     *   Category for dropwdown
     */
    categories: WidgetCategory[] | AppModeWidgetCategory[];
    /*
     *   Tooltip object for add widget menu preview
     */
    description?: DropdownTooltipText;
    /*
     * The requirements of the Portfolio that must be met to show data i.e. one order streamed or selected etc
     */
    portfolioSelectionRequirements: PortfolioSelectionRequirements;

    /*
     * Use this when portfolioSelectionRequirements is PositionFromAtLeastOneOrder, but the selection
     * requirements are different when not using the orders stream eg for the Fills Graph
     */
    portfolioSelectionRequirementsWithoutOrdersStream?: PortfolioSelectionRequirements;
    enableTitleEditing?: boolean;
    checkOrderColumnsOnConfigChange?: boolean;
}

export interface WidgetStrategyWithStreams extends WidgetStrategy {
    /*
     * Stream configuration for the WidgetsStreamService
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    streamConfigs: Array<WidgetStreamConfig<any, any>>;
}

/*
 * Implemented by widget strategies for widgets that need config saved in the layout
 */
export interface WidgetStrategyWithConfig<TConfig> extends WidgetStrategy {
    initialConfigState: TConfig;

    getTitle?: (
        symbolsService: SymbolsService,
        selection?: PortfolioSelection,
        config?: TConfig
    ) => Promise<string | undefined>;
}

export function isWidgetStrategyWithConfig<TConfig>(
    ws: WidgetStrategy
): ws is WidgetStrategyWithConfig<TConfig> {
    return !!(ws as WidgetStrategyWithConfig<TConfig>).initialConfigState;
}

export interface WidgetStrategyWithCustomManagedStream extends WidgetStrategy {
    managedStreamConfig: WidgetCustomManagedConfig<unknown>;
}
export interface WidgetStrategyWithStreamByRequestUid<TPayload>
    extends WidgetStrategy {
    managedStreamConfig: WidgetStreamByRequestUidConfig<TPayload>;
}
export interface WidgetStrategyWithRestApiByRequestUid<TPayload, TResponse>
    extends WidgetStrategy {
    managedStreamConfig: WidgetRestByRequestUidConfig<TPayload, TResponse>;
}

/*
 * Implemented by widgets strategies for widgets that may act as as input
 */
export interface PortfolioInputWidget extends WidgetStrategy {
    getPortfolioInput: (widgetKey: string) => PortfolioInput;
}
